import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import { RicosContext } from "./../../ricos-context";
import { ReactPlayer } from "./../../wix-react-player";
import {
  DownloadIcon,
  Image,
  PlaybackIcon,
  Slider,
  SLIDER_THUMB_VISIBILITY,
  SLIDER_TRACK_SIZE
} from "./../../ui-components";
import {
  AudioActionIcons,
  AudioDetails,
  AudioDuration,
  ContextMenu,
  SoundControl
} from "../components";
import {
  AUDIO_ACTION_NAMES,
  AUDIO_BI_VALUES,
  AUDIO_TYPES,
  downloadFile,
  playbackRates
} from "../consts";
import { AUDIO_SLIDER } from "../data-hooks";
import styles from "../statics/styles/audio.rtlignore-scss";
import { AUDIO_TYPE } from "../types";
const ReactPlayerWrapper = ({
  url,
  authorName,
  name,
  disableDownload,
  coverImage,
  helpers,
  disabled,
  isLoading,
  saveDurationToData
}) => {
  const { tabIndex, t, isMobile } = useContext(RicosContext);
  const [URL, setURL] = useState(url);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [played, setPlayed] = useState(0);
  const [volume, setVolume] = useState(1);
  const [lastVolume, setLastVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showPlaybackMenu, setShowPlaybackMenu] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isStoppedBySeek, setIsStoppedBySeek] = useState(false);
  const slimDesign = !authorName && !name;
  const reactPlayerRef = useRef(null);
  const onDownload = () => downloadFile(URL, name || "untitled");
  const playBackMenuData = playbackRates.map(({ text, rate }) => ({
    text,
    onClick: () => {
      setPlaybackRate(rate);
      setShowPlaybackMenu(false);
    },
    selected: playbackRate === rate
  }));
  const contextMenuOptions = {
    download: {
      text: "Download",
      onClick: () => {
        helpers?.onViewerAction?.(
          AUDIO_TYPE,
          AUDIO_ACTION_NAMES.fileDownloaded,
          AUDIO_BI_VALUES.click
        );
        onDownload();
        setShowContextMenu(false);
      },
      dataHook: "audio-download-icon",
      icon: DownloadIcon
    },
    playback: {
      text: "Playback Speed",
      onClick: () => {
        setShowContextMenu(false);
        setShowPlaybackMenu(true);
      },
      dataHook: "audio-playback-icon",
      icon: PlaybackIcon
    }
  };
  const contextMenuData = disableDownload ? [contextMenuOptions.playback] : [contextMenuOptions.download, contextMenuOptions.playback];
  const handleProgress = (state) => {
    if (!seeking) {
      setPlayed(state.played);
    }
  };
  const onEnded = () => helpers?.onViewerAction?.(
    AUDIO_TYPE,
    AUDIO_ACTION_NAMES.playAudio,
    AUDIO_BI_VALUES.PLAYBACK_COMPLETED
  );
  const handleDuration = (num) => {
    setDuration(num);
    saveDurationToData(num);
    helpers?.mediaPluginsDetails?.({
      pluginId: AUDIO_TYPE,
      creator: authorName,
      title: name,
      track_duration: num?.toString(),
      type: AUDIO_TYPES.custom,
      url
    });
  };
  const handleSeekChange = (num) => {
    isPlaying && setIsStoppedBySeek(true);
    handlePause();
    setSeeking(true);
    setPlayed(num);
  };
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleContextMenuClick = () => {
    showContextMenu || showPlaybackMenu ? onClickOutside() : setShowContextMenu(true);
  };
  const handleSeekMouseUp = (num) => {
    reactPlayerRef?.current?.seekTo(num);
    setSeeking(false);
    isStoppedBySeek && handlePlay();
    setIsStoppedBySeek(false);
  };
  const handleMute = () => {
    if (volume !== 0) {
      setLastVolume(volume);
      setVolume(0);
    } else {
      setVolume(lastVolume);
    }
  };
  const onClickOutside = () => {
    setShowContextMenu(false);
    setShowPlaybackMenu(false);
  };
  useEffect(() => {
    handlePause();
    setURL(url);
  }, [url]);
  useEffect(() => {
    reactPlayerRef.current?.seekTo(0);
  }, []);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", { className: classNames(styles.customPlayer, { [styles.slim]: slimDesign }) }, coverImage?.src && /* @__PURE__ */ React.createElement(
    Image,
    {
      className: styles.coverImage,
      uri: coverImage.src,
      width: 350,
      height: 350,
      displayMode: "fill",
      alt: t("Audio_Plugin_Cover_Alt")
    }
  ), /* @__PURE__ */ React.createElement("div", { className: styles.trackDetailsContainer }, !slimDesign && /* @__PURE__ */ React.createElement(AudioDetails, { authorName, title: name }), /* @__PURE__ */ React.createElement("div", { className: styles.controlsContainer }, /* @__PURE__ */ React.createElement("div", { className: classNames(styles.controlsGroup, styles.sliderContainer) }, /* @__PURE__ */ React.createElement(
    AudioActionIcons,
    {
      handlePlay,
      handlePause,
      isPlaying,
      disabled,
      isLoading,
      helpers
    }
  ), /* @__PURE__ */ React.createElement(
    Slider,
    {
      languageDir: "ltr",
      value: played,
      dataHook: AUDIO_SLIDER,
      onChange: handleSeekChange,
      onChangeCommitted: handleSeekMouseUp,
      step: "any",
      min: 0,
      max: 1,
      thumbVisibility: SLIDER_THUMB_VISIBILITY.hover,
      trackSize: SLIDER_TRACK_SIZE.small,
      liveSiteWiring: true,
      ariaLabel: "audio Slider",
      tabIndex,
      disabled: isLoading
    }
  ), /* @__PURE__ */ React.createElement(AudioDuration, { isLoading, played, duration })), /* @__PURE__ */ React.createElement("div", { className: styles.controlsGroup }, !isMobile && /* @__PURE__ */ React.createElement(
    SoundControl,
    {
      volume,
      handleMute,
      muted: volume === 0,
      handleVolumeChange: setVolume,
      disabled: isLoading
    }
  ), /* @__PURE__ */ React.createElement(
    "button",
    {
      className: styles.audioIconButton,
      tabIndex: -1,
      type: "button",
      disabled: isLoading
    },
    /* @__PURE__ */ React.createElement(
      ContextMenu,
      {
        onClickOutside,
        contextMenuData,
        playBackMenuData,
        showContextMenu,
        showPlaybackMenu,
        onClick: handleContextMenuClick
      }
    )
  ))))), /* @__PURE__ */ React.createElement(
    ReactPlayer,
    {
      previewAriaLabel: "Play audio",
      url: URL,
      ref: reactPlayerRef,
      playing: disabled ? false : isPlaying,
      playbackRate,
      onPause: handlePause,
      onPlay: handlePlay,
      width: 0,
      height: 0,
      controls: false,
      volume,
      onProgress: handleProgress,
      onDuration: handleDuration,
      "data-loaded": !isLoading,
      onEnded,
      config: {
        file: {
          forceAudio: true
        }
      }
    }
  ));
};
export {
  ReactPlayerWrapper
};
//# sourceMappingURL=react-player-wrapper.js.map
